import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { AssignmentLate, ArrowForward } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { requestGetUnassignedSummary } from '../redux/sagas/requests/project';

const TRIAGE_PATH = '/unassigned-triage';

const Columns = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'stretch',
}));

const Column = styled(Box)(({ theme }) => ({
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(1.5, 1.25),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    transition: 'background-color 120ms ease, border-color 120ms ease',
    '&:hover': {
        backgroundColor: theme.palette.surface.inset,
        borderColor: theme.palette.primary.main,
    },
}));

const ColumnLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
}));

const TotalNumber = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xxl,
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: 1.1,
    color: theme.palette.text.primary,
}));

const StatRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.75),
}));

const StatLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

const StatValue = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
}));

const Divider = styled(Box)(({ theme }) => ({
    height: 1,
    backgroundColor: theme.palette.divider,
    margin: theme.spacing(1, 0, 0.5),
}));

const Footer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1.5),
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

const EmptyStateIcon = styled(AssignmentLate)(({ theme }) => ({
    fontSize: 40,
    marginBottom: theme.spacing(1),
    opacity: 0.5,
}));

const emptyBucket = { total: 0, hasDept: 0, noDept: 0 };

const UnassignedWorkWidget = ({ widgetId, onRemove }) => {
    const navigate = useNavigate();

    const { data, isLoading, error } = useFetchQueue(widgetId, {
        apiCall: () => requestGetUnassignedSummary(),
        cacheKey: 'unassigned-summary',
    });

    const projects = data?.projects || emptyBucket;
    const tasks = data?.tasks || emptyBucket;
    const count = projects.total + tasks.total;
    const hasData = count > 0;

    const openBoard = (type) => navigate(type ? `${TRIAGE_PATH}?type=${type}` : TRIAGE_PATH);

    const renderColumn = (label, bucket, type) => (
        <Column onClick={() => openBoard(type)} role="button" tabIndex={0}>
            <ColumnLabel>{label}</ColumnLabel>
            <TotalNumber>{bucket.total}</TotalNumber>
            <Divider />
            <StatRow>
                <StatLabel>Unrouted</StatLabel>
                <StatValue>{bucket.noDept}</StatValue>
            </StatRow>
            <StatRow>
                <StatLabel>Awaiting owner</StatLabel>
                <StatValue>{bucket.hasDept}</StatValue>
            </StatRow>
        </Column>
    );

    return (
        <BaseWidget
            title={`Unassigned / No Responsible${!isLoading && count > 0 ? ` (${count})` : ''}`}
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
        >
            {!hasData ? (
                <EmptyState>
                    <EmptyStateIcon />
                    <Typography variant="body2">
                        Everything has an owner — nothing unassigned right now.
                    </Typography>
                </EmptyState>
            ) : (
                <>
                    <Columns>
                        {renderColumn('Projects', projects, 'project')}
                        {renderColumn('Tasks', tasks, 'task')}
                    </Columns>
                    <Footer>
                        <Button
                            size="small"
                            endIcon={<ArrowForward fontSize="small" />}
                            onClick={() => openBoard()}
                        >
                            Open triage board
                        </Button>
                    </Footer>
                </>
            )}
        </BaseWidget>
    );
};

export default UnassignedWorkWidget;
